<template>
  <div
    class="product-gallery"
    :class="`product-gallery--${responsiveLayout}`"
  >
    <div
      class="product-gallery__main-container"
      :class="{ 'product-gallery__main-container--alternate': alternateColumns }"
    >
      <product-carousel
        class="product-gallery__main-carousel"
        :current-index="currentIndex"
        :current-namespace="currentNamespace"
        :config="config.main"
        :layout="responsiveLayout"
        namespace="gallery"
        :product="product"
        :gallery="productGallery"
        :sizes="sizes"
        type="main"
        :zoom="zoom"
        :ratio="69/86"
        @update-current-index="handleUpdateCurrentIndex"
        @main-image-height="handleImageHeight"
      />
      <div
        v-if="genderToggleRequired()"
        class="product-gallery__gender-toggle-container"
      >
        <button
          v-for="(gender, index) of genderTitles"
          :key="index"
          class="product-gallery__gender-toggle text-other-button-s text-other-button-l-desktop"
          :class="{[$class('active')]: activeGender === hardcodedGenderTitles[index]}"
          type="button"
          :data-gender="hardcodedGenderTitles[index]"
          @click="toggleGender($event.target)"
          v-text="gender"
        />
      </div>
    </div>
    <div
      v-if="showThumbnails"
      class="product-gallery__thumbnails-container"
    >
      <product-carousel
        class="product-gallery__thumbnails-carousel"
        :current-index="currentIndex"
        :current-namespace="currentNamespace"
        :config="config.thumbnails"
        :layout="responsiveLayout"
        :main-image-height="mainImageHeight"
        min-max="100-300"
        namespace="gallery"
        :product="product"
        :gallery="productGallery"
        sizes="102px"
        type="thumbnails"
        @update-current-index="handleUpdateCurrentIndex"
      />
    </div>
    <!-- Product gallery modal -->
    <overlay
      v-if="zoom"
      key="productGalleryModal"
      block-class="product-gallery-modal-overlay"
      direction="up"
      namespace="productGalleryModal"
      :show-overlay="false"
      :template="false"
      type="modal"
    >
      <template #body="overlay">
        <product-gallery-modal
          v-if="getComponentVisible('product-gallery-modal')"
          v-cloak
          :current-index="currentIndex"
          :current-namespace="currentNamespace"
          namespace="modal"
          :overlay="overlay"
          :product="product"
          :gallery="productGallery"
          @update-current-index="handleUpdateCurrentIndex"
          @update-current-namespace="handleUpdateCurrentNamespace"
        />
      </template>
    </overlay>
  </div>
</template>

<script>

/**
 * Vue: Product gallery (product-gallery)
 * -----------------------------------------------------------------------------
 * Wrapper for product gallery carousel.
 *
 * @param {Number} currentIndex - Current carousel index.
 * @param {String} currentNamespace - Current carousel namespace.
 * @param {String} layout - Product gallery layout, either `horizontal`,
 * `vertical`, `tabs`, or `grid`, supports a combination in the format
 * [mobile layout]-[desktop layout].
 * @param {Object} product - Local product object.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 * @param {Boolean} [zoom] - Enable zoom functionality.
 *
 * @emits updateCurrentIndex - Emit update current carousel index event.
 * @emits updateCurrentNamespace - Emit update current carousel namespace event.
 *
 */
import { defineAsyncComponent } from 'vue'
import { mapActions, mapState } from 'vuex'
import Overlay from '~global/overlay/overlay'
import ProductCarousel from '~async/product-carousel/product-carousel'
import { values as breakpoints } from '~/config/breakpoints'
export default {
  name: 'ProductGallery',
  components: {
    Overlay,
    ProductCarousel,
    ProductGalleryModal: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.product-gallery-modal' */
        '~async/product-gallery-modal/product-gallery-modal'
      ),
    }),
  },
  props: {
    currentIndex: {
      type: Number,
      default: 0,
    },
    currentNamespace: {
      type: String,
      default: 'gallery',
      validator: (value) => {
        return ['gallery', 'modal'].includes(value)
      },
    },
    layout: {
      type: String,
      default: 'horizontal',
      validator: (value) => {
        return value.split('-').every((part) => ['grid', 'horizontal', 'tabs', 'vertical'].includes(part))
      },
    },
    product: {
      type: [Boolean, Object],
      default: false,
    },
    variant: {
      type: [Boolean, Object],
      default: () => ({}),
    },
    zoom: {
      type: Boolean,
      default: true,
    },
    alternateColumns: {
      type: Boolean,
      default: false,
    },
  },
  emits: {
    'update-model-info': (payload) => typeof payload === 'string',
    'update-current-index': (payload) => typeof payload === 'number',
    'update-current-namespace': (payload) => ['gallery', 'modal'].includes(payload),
  },
  data() {
    return {
      hardcodedGenderTitles: [
        'female',
        'male',
      ],
      genderTitles: [
        this.$string('product.gallery.female'),
        this.$string('product.gallery.male'),
      ],
      activeGender: '',
      mainImageHeight: 0,
    }
  },
  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),
    productGallery() {
      const productMedia = !this.product?.media && this.product?.images
        ? this.product.images || []
        : this.product.media || []
      if (this.genderToggleRequired()) {
        return productMedia.filter((item) => this.emitActiveGenderImages(item) || item.mediaType === 'video')
      }
      return productMedia
    },

    /**
     * Compute carousel configs.
     * - The aim here is to convert the responsive layout into the breakpoints
     *   object supported by Splide.
     * @returns {Object}
     */
    config() {
      const mainConfigs = {
        grid: {
          arrows: false,
          destroy: true,
          pagination: false,
        },
        horizontal: {
          arrows: false,
          destroy: false,
          pagination: false,
        },
        tabs: {
          arrows: false,
          destroy: false,
          pagination: true,
        },
        vertical: {
          arrows: false,
          drag: false,
          destroy: false,
          pagination: false,
        },
      }
      const thumbnailsConfigs = {
        shared: {
          arrows: false,
          drag: false,
          focus: 1,
          pagination: false,
          drag: true,
          perMove: 5,
          perPage: 6.5,
          type: 'slide',
        },
        horizontal: {
          gap: 'var(--spacing-m)',
        },
        vertical: {
          direction: 'ttb',
          fixedHeight: 'fit-content',
          gap: '6px',
          height: `${this.mainImageHeight}px`,
          trimSpace: true,
        },
        verticalBreakpoints: {
          [breakpoints.m]: {
            height: 'calc((70px + var(--spacing-xs)) * 5)',
          },
          [breakpoints.l]: {
            height: 'calc((70px + var(--spacing-xs)) * 3)',
          },
          [breakpoints.xl]: {
            height: 'calc((70px + var(--spacing-xs)) * 5)',
          },
        },
      }

      /**
       * Set configs.
       * - No difference between mobile and desktop layouts.
       */
      if (!this.layout.includes('-')) {
        const config = {
          main: mainConfigs[this.layout],
          thumbnails: {
            ...thumbnailsConfigs.shared,
            ...thumbnailsConfigs[this.layout],
          },
        }
        if (this.layout === 'vertical') {
          config.thumbnails.breakpoints = thumbnailsConfigs.verticalBreakpoints
        }
        return config
      }

      /**
       * Set mobile and desktop layouts independently.
       */
      const mobileLayout = this.layout.split('-')[0]
      const desktopLayout = this.layout.split('-')[1]
      const config = {
        main: {
          ...mainConfigs[mobileLayout],
          breakpoints: {
            [breakpoints.l]: {
              ...mainConfigs[desktopLayout],
            },
          },
        },
        thumbnails: {
          ...thumbnailsConfigs.shared,
          ...thumbnailsConfigs[mobileLayout],
          breakpoints: {
            [breakpoints.l]: {
              ...thumbnailsConfigs[desktopLayout],
            },
          },
        },
      }

      /**
       * Add vertical layout breakpoints.
       */
      if (mobileLayout === 'vertical') {
        config.thumbnails.breakpoints = thumbnailsConfigs.verticalBreakpoints[breakpoints.m]
      }
      if (desktopLayout === 'vertical') {
        config.thumbnails.breakpoints = {
          [breakpoints.l]: {
            ...config.thumbnails.breakpoints[breakpoints.l],
            ...thumbnailsConfigs.verticalBreakpoints[breakpoints.l],
          },
          [breakpoints.xl]: thumbnailsConfigs.verticalBreakpoints[breakpoints.xl],
        }
      }

      /**
       * Return config.
       */
      return config
    },

    /**
     * Compute media array.
     * @returns {Array}
     */
    media() {
      return !this.product?.media && this.product?.images
        ? this.product.images || []
        : this.product.media || []
    },

    /**
     * Compute responsive layout based on mobile.
     * @returns {String}
     */
    responsiveLayout() {
      if (!this.layout.includes('-')) {
        return this.layout
      }
      return this.isMobile
        ? this.layout.split('-')[0]
        : this.layout.split('-')[1]
    },

    /**
     * Compute whether to show thumbnails carousel.
     * @returns {Boolean}
     */
    showThumbnails() {
      return (
        this.media.length > 1 &&
        (this.responsiveLayout === 'horizontal' || this.responsiveLayout === 'vertical')
      )
    },

    /**
     * Compute responsive image sizes.
     * @returns {String}
     */
    sizes() {
      return `(min-width: ${breakpoints.l}px) 50vw, 100vw`
    },
  },
  watch: {

    /**
     * Watch variant to show matching image if it exists.
     */
    variant() {
      if (!this.variant) {
        return
      }

      /**
       * Matching image with variant only works when using images array.
       * - Media items do not return associated variant ID.
       * - Media and images array item IDs do not match so it's not possible to
       *   match.
       */
      const matchedImageIndex = this.media.findIndex((mediaItem) => {
        return mediaItem.variant === this.variant.id
      })
      if (matchedImageIndex < 0) {
        return
      }
      this.$emit('update-current-index', matchedImageIndex)
    },
  },
  mounted() {
    this.activeGender = localStorage.activeGender ? localStorage.getItem('activeGender') : 'female'

    window._conv_q = window._conv_q || [];
    window._conv_q.push(["executeExperiment","1004116633"]);
  },
  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
    }),

    /**
     * Updatea the mainImageHeight value that is used in vertical thumbnail carousel
     */
    handleImageHeight(imageHeight) {
      this.mainImageHeight = imageHeight.offsetHeight
    },

    /**
     * Emit update current carousel index event.
     * @param {Number} newIndex - New media index.
     */
    handleUpdateCurrentIndex(newIndex) {
      this.$emit('update-current-index', newIndex)
    },

    /**
     * Emit update current carousel namespace event.
     * @param {Number} newNamespace - New active namespace.
     */
    handleUpdateCurrentNamespace(newNamespace) {
      this.$emit('update-current-namespace', newNamespace)
    },

    /**
     * Toggle gender selector.
     * @param {String} target - selected gender button.
     */
    toggleGender(target) {
      if (target.dataset.gender === this.activeGender) {
        return
      }

      this.activeGender = target.dataset.gender
      localStorage.setItem('activeGender', target.dataset.gender)
      this.$emit('update-model-info', target.dataset.gender)
    },

    /**
     * Check if at least one alt tag contains word - MALE.
     */
    genderToggleRequired() {
      const productMedia = !this.product?.media && this.product?.images
        ? this.product.images || []
        : this.product.media || []
      const altTagsCount = productMedia.filter((item) => item.alt.includes('*MALE*')).length
      return Boolean(
        productMedia.length > 1 &&
          productMedia.length > altTagsCount &&
          altTagsCount > 0,
      )
    },

    /**
     * Return required method to search for alt values
     * @param {Object} item - image object.
     */
    emitActiveGenderImages(item) {
      if (this.activeGender === 'male') {
        return item.alt.includes(`*${this.activeGender.toUpperCase()}*`)
      }
      return !item.alt.includes(`*MALE*`)
    },
  },
}
</script>

<style lang="scss">
@import '@/components/splide';
@import './product-gallery';
@import './product-gallery-grid';
@import './product-gallery-tabs';
// Disable unused layout stylesheets
// @import './product-gallery-horizontal';
@import './product-gallery-vertical';
</style>
